import React, { useState } from 'react';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import AnalysisStatus from './components/AnalysisStatus';
import ReportViewer from './components/ReportViewer';
import NewAnalysisModal from './components/NewAnalysisModal';

interface Brand {
  id: number;
  name: string;
  website_url: string;
  status: string;
  created_at: string;
}

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [brandName, setBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [brand, setBrand] = useState<Brand | null>(null);
  const [reportId, setReportId] = useState<number | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dashboard.llmseo.fi';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
  
    try {
      const response = await axios.post<Brand>(`${API_BASE_URL}/api/brands`, {
        name: brandName,
        website_url: websiteUrl
      });
  
      setBrand(response.data);
      setBrandName('');
      setWebsiteUrl('');
      setIsModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalysisComplete = (completedReportId: number) => {
    setReportId(completedReportId);
    setShowReport(true);
  };

  const handleNewAnalysis = () => {
    setBrand(null);
    setReportId(null);
    setShowReport(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="flex">
        <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
        <div className="flex-1 flex flex-col">
          <Header onNewAnalysis={() => setIsModalOpen(true)} />
          <main className="flex-1 p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            {brand && !showReport && (
              <AnalysisStatus 
                brandId={brand.id} 
                onComplete={handleAnalysisComplete}
              />
            )}
            {showReport && reportId && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Analysis Complete</h3>
                  <button
                    onClick={handleNewAnalysis}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Analyze Another Brand
                  </button>
                </div>
                <ReportViewer reportId={reportId} />
              </div>
            )}
            {!brand && !showReport && (
              <Dashboard />
            )}
          </main>
        </div>
      </div>
      <NewAnalysisModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        brandName={brandName}
        setBrandName={setBrandName}
        websiteUrl={websiteUrl}
        setWebsiteUrl={setWebsiteUrl}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </div>
  );
}

export default App;
